import { useEffect, useState, useCallback } from "react";
import { hitApi } from "../../components/commonAction";
import AffiliatePageLayout from "./AffiliatePageLayout";

const RANGES = [
    { value: 7,   label: "Last 7 days" },
    { value: 30,  label: "Last 30 days" },
    { value: 90,  label: "Last 90 days" },
    { value: 365, label: "Last year" },
];

export default function Analytics({ pageTitle }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState(30);

    const load = (days) => {
        setLoading(true);
        hitApi({
            url: `affiliate/analytics?range=${days}`,
            method: "GET", data: {}, alert: false,
        }, (resp) => {
            setLoading(false);
            if (resp?.status) setData(resp.data || null);
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(range); }, [range]);

    const totals = data?.totals || {};
    const daily = data?.series?.daily || [];
    const byTier = data?.breakdown?.byTier || [];
    const byPlan = data?.breakdown?.byPlan || [];

    return (
        <AffiliatePageLayout pageTitle={pageTitle} heading="Affiliate Analytics">
            <div className="pxl-analytics-toolbar">
                <div className="pxl-analytics-range">
                    {RANGES.map(r => (
                        <button
                            key={r.value}
                            type="button"
                            className={`pxl-analytics-range-btn ${range === r.value ? "isActive" : ""}`}
                            onClick={() => setRange(r.value)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <AnalyticsSkeleton />
            ) : !data ? (
                <div className="pxl-status-banner pxl-status-banner--warning">No analytics data available.</div>
            ) : (
                <>
                    <div className="pxl-stat-grid">
                        <StatCard label="Clicks"          value={fmtNum(totals.clicks)} />
                        <StatCard label="Conversions"     value={fmtNum(totals.conversions)} />
                        <StatCard label="Conversion rate" value={`${(totals.conversionRate || 0).toFixed(2)}%`} />
                        <StatCard label="Earnings"        value={fmtMoney(totals.earnings)}
                                  negative={Number(totals.earnings) < 0} />
                    </div>

                    <DailyChart daily={daily} />

                    <div className="row g-3 mt-1">
                        <div className="col-md-6">
                            <BreakdownPanel
                                title="Top tiers"
                                rows={byTier.map(r => ({
                                    label: r.tierName || `tier ${r.tierCode || "?"}`,
                                    sub: r.tierCode ? `code: ${r.tierCode}` : "",
                                    conversions: r.conversions,
                                    earnings: r.earnings,
                                }))}
                            />
                        </div>
                        <div className="col-md-6">
                            <BreakdownPanel
                                title="Top product plans"
                                rows={byPlan.map(r => ({
                                    label: r.productName ? `${r.productName} — ${r.planName}` : (r.planName || "(unknown)"),
                                    sub: r.productCode && r.planCode ? `${r.productCode}/${r.planCode}` : "",
                                    conversions: r.conversions,
                                    earnings: r.earnings,
                                }))}
                            />
                        </div>
                    </div>
                </>
            )}
        </AffiliatePageLayout>
    );
}

function AnalyticsSkeleton() {
    return (
        <>
            {/* Toolbar shimmer */}
            <div style={{ marginBottom: 20 }}>
                <span className="pxl-sk" style={{ width: 220, height: 36, borderRadius: 8, display: 'inline-block' }} />
            </div>

            {/* Stat grid */}
            <div className="pxl-stat-grid" style={{ marginBottom: 20 }}>
                {[0, 1, 2, 3].map(i => (
                    <div className="pxl-sk-card" key={i}>
                        <span className="pxl-sk" style={{ width: '55%', height: 10, display: 'block' }} />
                        <span className="pxl-sk" style={{ width: '45%', height: 26, display: 'block', marginTop: 12 }} />
                    </div>
                ))}
            </div>

            {/* Chart panel */}
            <div className="pxl-sk-panel" style={{ marginBottom: 20 }}>
                <div className="pxl-sk-panel-header">
                    <span className="pxl-sk" style={{ width: 160, height: 13, display: 'inline-block' }} />
                </div>
                <div className="pxl-sk-panel-body">
                    {/* Legend row */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                        <span className="pxl-sk" style={{ width: 60, height: 10, display: 'inline-block' }} />
                        <span className="pxl-sk" style={{ width: 60, height: 10, display: 'inline-block' }} />
                    </div>
                    {/* Chart area */}
                    <span className="pxl-sk" style={{ width: '100%', height: 140, borderRadius: 8, display: 'block' }} />
                    {/* Axis row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                        <span className="pxl-sk" style={{ width: 40, height: 9, display: 'inline-block' }} />
                        <span className="pxl-sk" style={{ width: 40, height: 9, display: 'inline-block' }} />
                    </div>
                </div>
            </div>

            {/* Two breakdown panels */}
            <div className="row g-3 mt-1">
                {[0, 1].map(i => (
                    <div className="col-md-6" key={i}>
                        <div className="pxl-sk-panel" style={{ height: '100%' }}>
                            <div className="pxl-sk-panel-header">
                                <span className="pxl-sk" style={{ width: 120, height: 13, display: 'inline-block' }} />
                            </div>
                            <div className="pxl-sk-panel-body">
                                {[0, 1, 2, 3].map(j => (
                                    <div key={j} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                        <span className="pxl-sk" style={{ width: '50%', height: 13, display: 'inline-block' }} />
                                        <span className="pxl-sk" style={{ width: '20%', height: 13, display: 'inline-block' }} />
                                        <span className="pxl-sk" style={{ width: '20%', height: 13, display: 'inline-block' }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

function StatCard({ label, value, negative }) {
    return (
        <div className="pxl-stat-card">
            <div className="pxl-stat-card-label">{label}</div>
            <div className={`pxl-stat-card-value ${negative ? "is-negative" : ""}`}>{value}</div>
        </div>
    );
}

function DailyChart({ daily }) {
    const [tooltip, setTooltip] = useState(null);

    const showTooltip = useCallback((e, d) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTooltip({ x: r.left + r.width / 2, y: r.top, d });
    }, []);

    if (!daily.length) return null;

    const maxClicks   = Math.max(1,    ...daily.map(d => d.clicks));
    const maxEarnings = Math.max(0.01, ...daily.map(d => d.earnings));
    const yTicks      = [maxClicks, Math.round(maxClicks * 0.75), Math.round(maxClicks * 0.5), Math.round(maxClicks * 0.25), 0];
    const step        = Math.max(1, Math.ceil(daily.length / 5));
    const xDates      = daily.filter((_, i) => i === 0 || i === daily.length - 1 || i % step === 0);

    return (
        <div className="pxl-panel">
            <div className="pxl-panel-header">Daily activity</div>
            <div className="pxl-panel-body">
                <div className="pxl-analytics-chart-legend">
                    <span><i className="pxl-legend-dot pxl-legend-dot--clicks" /> Clicks</span>
                    <span><i className="pxl-legend-dot pxl-legend-dot--conversions" /> Conversions</span>
                    <span><i className="pxl-legend-dot pxl-legend-dot--earnings" /> Earnings</span>
                </div>
                <div className="pxl-chart-wrap">
                    <div className="pxl-chart-yaxis">
                        {yTicks.map((v, i) => <span key={i}>{v}</span>)}
                    </div>
                    <div className="pxl-chart-inner">
                        <div className="pxl-chart-grid">
                            {yTicks.map((_, i) => (
                                <div key={i} className={`pxl-chart-grid-line${i === yTicks.length - 1 ? ' pxl-chart-grid-line--base' : ''}`} />
                            ))}
                        </div>
                        <div className="pxl-analytics-chart">
                            {daily.map(d => (
                                <div className="pxl-analytics-chart-col" key={d.date}
                                    onMouseEnter={e => showTooltip(e, d)}
                                    onMouseLeave={() => setTooltip(null)}>
                                    <div className="pxl-analytics-chart-bars">
                                        <div className="pxl-analytics-chart-bar pxl-analytics-chart-bar--clicks"
                                            style={{ height: `${(d.clicks / maxClicks) * 100}%` }} />
                                        <div className="pxl-analytics-chart-bar pxl-analytics-chart-bar--conversions"
                                            style={{ height: `${(d.conversions / maxClicks) * 100}%` }} />
                                        <div className="pxl-analytics-chart-bar pxl-analytics-chart-bar--earnings"
                                            style={{ height: `${(Math.max(0, d.earnings) / maxEarnings) * 100}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="pxl-analytics-chart-divider" />
                        <div className="pxl-analytics-chart-axis">
                            {xDates.map(d => <span key={d.date}>{shortDate(d.date)}</span>)}
                        </div>
                    </div>
                </div>
            </div>
            {tooltip && (
                <div className="pxl-chart-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                    <div className="pxl-chart-tooltip-date">{shortDate(tooltip.d.date)}</div>
                    <div className="pxl-chart-tooltip-row">
                        <span className="pxl-chart-tooltip-label"><span className="pxl-chart-tooltip-dot" style={{ background: '#7ea8e0' }} />Clicks</span>
                        <span className="pxl-chart-tooltip-val">{tooltip.d.clicks}</span>
                    </div>
                    <div className="pxl-chart-tooltip-row">
                        <span className="pxl-chart-tooltip-label"><span className="pxl-chart-tooltip-dot" style={{ background: '#9b4de8' }} />Conversions</span>
                        <span className="pxl-chart-tooltip-val">{tooltip.d.conversions}</span>
                    </div>
                    <div className="pxl-chart-tooltip-row">
                        <span className="pxl-chart-tooltip-label"><span className="pxl-chart-tooltip-dot" style={{ background: '#2db67c' }} />Earnings</span>
                        <span className="pxl-chart-tooltip-val">${Number(tooltip.d.earnings).toFixed(2)}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function BreakdownPanel({ title, rows }) {
    return (
        <div className="pxl-panel" style={{ height: "100%" }}>
            <div className="pxl-panel-header">{title}</div>
            <div className="pxl-panel-body pxl-panel-body--flush">
                {rows.length === 0 ? (
                    <div className="text-muted" style={{ padding: 20 }}>No conversions in this window.</div>
                ) : (
                    <table className="pxl_table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th>{title.includes("tier") ? "Tier" : "Plan"}</th>
                                <th style={{ textAlign: "right" }}>Conv.</th>
                                <th style={{ textAlign: "right" }}>Earnings</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.slice(0, 10).map((r, i) => (
                                <tr key={i}>
                                    <td>
                                        <p style={{ margin: 0 }}>{r.label}</p>
                                        {r.sub && <p style={{ color: "#8a8fa6", fontSize: 11, margin: 0, fontFamily: "ui-monospace, Menlo, monospace" }}>{r.sub}</p>}
                                    </td>
                                    <td style={{ textAlign: "right" }}><p>{r.conversions}</p></td>
                                    <td style={{ textAlign: "right" }}><p>${Number(r.earnings).toFixed(2)}</p></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function fmtMoney(v) { return `$${Number(v || 0).toFixed(2)}`; }
function fmtNum(v)   { return Number(v || 0).toLocaleString(); }
function shortDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
